import { useState, useRef, useEffect, useMemo } from 'react';
import { useActionStore } from '../../store/actionStore';
import { useUserStore } from '../../store/userStore';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../../lib/queryKeys';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  MessageSquare,
  FileText,
  Presentation,
  Calendar,
  BarChart2,
  ExternalLink,
  RefreshCw,
  RefreshCcw,
  Send,
  Copy,
  Check,
  CheckCircle2,
  Loader2,
  ChevronRight,
  Hash,
  Mail,
  AlertCircle,
  AlertTriangle,
  Bold,
  Italic,
  Strikethrough,
  Code,
  Heading1,
  Heading2,
  List,
  Eye,
  PencilLine,
} from 'lucide-react';
import type { SuggestedAction, ActionResult, ActionType, ActionCompletion } from '../../types';
import { executeAction, getIntegrationResources, getProject } from '../../lib/api';
import { track } from '../../lib/telemetry';
import { StarRating } from '../feedback/StarRating';
import { FEATURE_KEYS } from '../../lib/feedback';
import { Markdown } from '../Markdown';
import { deriveActionTriggerState, isBlockingCompletion, completionByLine } from '../../lib/actionCompletion';

// Strips markdown syntax down to readable plain text — for pasting into
// targets that don't render markdown (e.g. email clients). Deliberately
// simple regex-based stripping rather than a full markdown AST walk, since
// the inputs here are AI-generated content (headings, lists, bold/italic,
// links, code) rather than arbitrary user markdown.
function markdownToPlainText(md: string): string {
  return md
    .replace(/```[\s\S]*?```/g, (block) => block.replace(/```\w*\n?|```/g, ''))
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1 ($2)')
    .replace(/(\*\*|__)(.*?)\1/g, '$2')
    .replace(/(\*|_)(.*?)\1/g, '$2')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/^\s*>\s?/gm, '')
    .replace(/^\s*[-*+]\s+/gm, '- ')
    .replace(/^\s*\d+\.\s+/gm, '')
    .replace(/^-{3,}$/gm, '')
    .trim();
}

interface ActionModalProps {
  action: SuggestedAction;
  projectId?: string;
  ticketId?: string;
  onClose: () => void;
  onCompleted?: (result: ActionResult) => void;
  completedResult?: ActionResult | null;
  /**
   * Server-authoritative completion record for this actionId (from the
   * source cache doc's `completions` map), passed down by whichever trigger
   * (ActionButton / WhatsOnDeck's ActionRow / AdaptiveView's ActionCard)
   * rendered this modal. This is what survives a page reload and is shared
   * across users — completedResult above is session-local client cache only.
   */
  completion?: ActionCompletion;
}

// ── Destination identity per action type ──────────────────────────────────────
//
// `brand` carries the destination application's official mark (vendor
// assets only — the Google product icons are the official 2026 Workspace
// PNGs at native 1x/2x, Slack's is its official SVG). Actions whose real
// destination is ambiguous or internal (ticket comments go to Jira OR
// Linear depending on the ticket's source; status updates are internal;
// the generated-email path sends via Resend, not Gmail) deliberately keep
// a neutral lucide glyph — showing a brand we can't guarantee would be a
// lie about where the content is going.
//
// `bodyFormat` states what the DESTINATION can actually render, grounded
// in the executors (backend/app/routers/actions.py):
//   - 'mrkdwn'  — Slack chat.postMessage renders Slack mrkdwn (*bold*,
//                 _italic_, ~strike~, `code`). Markdown ** would show
//                 literally, so the toolbar inserts mrkdwn.
//   - 'markdown'— _exec_create_doc parses #/## headings, - bullets,
//                 **bold**, *italic* into styled Doc segments.
//   - 'plain'   — the executor transmits raw text (e.g. Jira ADF comment
//                 paragraphs). No formatting affordances: a control that
//                 silently degrades on send is worse than none.

interface ActionBrand { src: string; srcSet?: string }

const SLACK_BRAND: ActionBrand = { src: '/slack-icon.svg' };
const DOCS_BRAND: ActionBrand = { src: '/integrations/google/docs-32.png', srcSet: '/integrations/google/docs-32.png 1x, /integrations/google/docs-64.png 2x' };
const SLIDES_BRAND: ActionBrand = { src: '/integrations/google/slides-32.png', srcSet: '/integrations/google/slides-32.png 1x, /integrations/google/slides-64.png 2x' };
const CALENDAR_BRAND: ActionBrand = { src: '/integrations/google/calendar-32.png', srcSet: '/integrations/google/calendar-32.png 1x, /integrations/google/calendar-64.png 2x' };

type BodyFormat = 'mrkdwn' | 'markdown' | 'plain';

const ACTION_META: Record<ActionType, {
  icon: React.ElementType;
  colour: string;
  label: string;
  brand?: ActionBrand;
  bodyFormat?: BodyFormat;
  width?: 'md' | 'lg' | '2xl';
}> = {
  slack_message:          { icon: MessageSquare,  colour: 'text-[#4A154B]',   label: 'Slack Message',       brand: SLACK_BRAND,    bodyFormat: 'mrkdwn',   width: '2xl' },
  slack_dm:               { icon: MessageSquare,  colour: 'text-[#4A154B]',   label: 'Slack DM',            brand: SLACK_BRAND,    bodyFormat: 'mrkdwn',   width: '2xl' },
  create_doc:             { icon: FileText,       colour: 'text-blue-600',    label: 'Google Doc',          brand: DOCS_BRAND,     bodyFormat: 'markdown', width: '2xl' },
  create_slides:          { icon: Presentation,   colour: 'text-amber-600',   label: 'Google Slides',       brand: SLIDES_BRAND,   bodyFormat: 'plain',    width: '2xl' },
  create_calendar_event:  { icon: Calendar,       colour: 'text-emerald-600', label: 'Google Calendar',     brand: CALENDAR_BRAND, bodyFormat: 'plain',    width: 'lg' },
  post_ticket_comment:    { icon: MessageSquare,  colour: 'text-primary',     label: 'Ticket Comment',      bodyFormat: 'plain',    width: '2xl' },
  update_ticket_status:       { icon: RefreshCw,  colour: 'text-indigo-600',  label: 'Update Status' },
  bulk_update_ticket_status:  { icon: RefreshCcw, colour: 'text-indigo-600',  label: 'Bulk Update Tickets' },
  status_report:          { icon: BarChart2,      colour: 'text-violet-600',  label: 'Status Report',       width: '2xl' },
  open_url:               { icon: ExternalLink,   colour: 'text-gray-600',    label: 'Open Link' },
};

const MODAL_WIDTH: Record<'md' | 'lg' | '2xl', string> = {
  md: 'max-w-md',
  lg: 'max-w-lg',
  '2xl': 'max-w-2xl',
};

// ── Slack mrkdwn preview ──────────────────────────────────────────────────────
// Minimal faithful rendering of what chat.postMessage will display. The
// input is HTML-escaped first; every tag below is one we inject ourselves.
function mrkdwnToHtml(text: string): string {
  let t = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  // Code spans are extracted to placeholders FIRST so the inline style
  // regexes below can never match characters inside a code literal (an
  // underscore inside `at_risk` must not open an italic run) or inside
  // the HTML we emit for them.
  // Sentinel from the Unicode private-use area — cannot occur in escaped
  // user text, so restoring placeholders can never collide with content.
  const SENTINEL = '';
  const codes: string[] = [];
  const stash = (html: string) => SENTINEL + (codes.push(html) - 1) + SENTINEL;
  t = t.replace(/```([\s\S]*?)```/g, (_, c: string) => stash(`<pre class="mrkdwn-pre">${c}</pre>`));
  t = t.replace(/`([^`\n]+)`/g, (_, c: string) => stash(`<code class="mrkdwn-code">${c}</code>`));
  t = t.replace(/\*([^*\n]+)\*/g, '<strong>$1</strong>');
  t = t.replace(/_([^_\n]+)_/g, '<em>$1</em>');
  t = t.replace(/~([^~\n]+)~/g, '<del>$1</del>');
  t = t.replace(/&lt;(https?:\/\/[^|\s]+)\|([^&]+)&gt;/g, '<a href="$1" target="_blank" rel="noopener noreferrer">$2</a>');
  t = t.replace(/(\d+)/g, (_, i: string) => codes[Number(i)]);
  return t.replace(/\n/g, '<br/>');
}

// Google Docs preview.
//
// _exec_create_doc (backend/app/routers/actions.py) does NOT run this
// content through a full markdown engine -- it hand-walks the text with its
// own small parser (_build_doc_requests / parse_inline) that supports only:
// '#'/'##'/'###' headings (the FIRST '# ' becomes the cover-page title and
// is never a body heading), '-'/'*' bullets (one nested level via 2+
// leading spaces), '**bold**', '*italic*'/'_italic_', '__underline__' (NOT
// bold -- a deliberate backend choice), a bare '---' rule, and `code span`
// (stripped to plain text, no monospace styling). It does NOT understand
// links, blockquotes, or ordered lists -- those lines land in the Doc as
// literal text, syntax and all.
//
// Previously this destination's preview ran through the generic
// react-markdown-based <Markdown> component (full CommonMark) -- a superset
// of what actually gets created. Two concrete, verified divergences: (1)
// '__text__' rendered as bold in the old preview but is UNDERLINE in the
// real Doc, and (2) a backtick code span rendered as a real <code> box in
// the preview while the executor did not protect code spans from its own
// bold/italic regexes at all (fixed alongside this, in
// _build_doc_requests). Per "the preview must match the executor", this
// renderer mirrors the backend parser -- not the other way around.
function docsMarkdownToHtml(content: string): string {
  const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  // Mirrors _build_doc_requests' parse_inline exactly: code span extracted
  // first (protected via sentinel stash, same technique as mrkdwnToHtml
  // above) so an underscore inside `at_risk`/`onboarding_demo` can never be
  // mistaken for italic syntax, then __underline__, **bold**, *italic*,
  // _italic_ in that priority order.
  const inline = (raw: string): string => {
    let t = esc(raw);
    const SENTINEL = String.fromCharCode(0xe000);
    const stashed: string[] = [];
    const stash = (html: string) => SENTINEL + (stashed.push(html) - 1) + SENTINEL;
    t = t.replace(/`([^`\n]+?)`/g, (_, c: string) => stash(c));
    t = t.replace(/__(.+?)__/g, (_, c: string) => stash(`<u>${c}</u>`));
    t = t.replace(/\*\*(.+?)\*\*/g, (_, c: string) => stash(`<strong>${c}</strong>`));
    t = t.replace(/\*([^*\n]+?)\*/g, (_, c: string) => stash(`<em>${c}</em>`));
    t = t.replace(/_([^_\n]+?)_/g, (_, c: string) => stash(`<em>${c}</em>`));
    t = t.replace(new RegExp(SENTINEL + '(\\d+)' + SENTINEL, 'g'), (_, i: string) => stashed[Number(i)]);
    return t;
  };

  let docTitleConsumed = false;
  const out: string[] = [];
  let listLevel: 0 | 1 | 2 = 0;
  const closeLists = (to: 0 | 1 | 2) => {
    while (listLevel > to) {
      out.push('</ul>');
      listLevel = (listLevel - 1) as 0 | 1 | 2;
    }
  };

  for (const rawLine of content.split('\n')) {
    const line = rawLine.replace(/\s+$/, '');

    if (!line) { closeLists(0); continue; }

    if (line.startsWith('# ') && !line.startsWith('## ')) {
      closeLists(0);
      // First '# ' becomes the cover-page title server-side -- it never
      // appears as a body heading in the real Doc, so it must not preview
      // as one here.
      if (!docTitleConsumed) { docTitleConsumed = true; continue; }
      out.push(`<h1>${inline(line.slice(2))}</h1>`);
      continue;
    }
    if (line.startsWith('## ') && !line.startsWith('### ')) {
      closeLists(0);
      out.push(`<h2>${inline(line.slice(3))}</h2>`);
      continue;
    }
    if (line.startsWith('### ')) {
      closeLists(0);
      out.push(`<h3>${inline(line.slice(4))}</h3>`);
      continue;
    }
    if (line === '---') {
      closeLists(0);
      out.push('<hr />');
      continue;
    }
    if (/^\s{2,}[*-] /.test(line)) {
      if (listLevel < 1) { out.push('<ul>'); listLevel = 1; }
      if (listLevel < 2) { out.push('<ul>'); listLevel = 2; }
      out.push(`<li>${inline(line.replace(/^\s*[*-] /, ''))}</li>`);
      continue;
    }
    if (/^[*-] /.test(line)) {
      closeLists(1);
      if (listLevel < 1) { out.push('<ul>'); listLevel = 1; }
      out.push(`<li>${inline(line.slice(2))}</li>`);
      continue;
    }
    closeLists(0);
    out.push(`<p>${inline(line)}</p>`);
  }
  closeLists(0);
  return out.join('');
}

// ── Formatting toolbar actions, scoped to what each destination renders ───────

interface FormatAction {
  title: string;
  icon: React.ElementType;
  before: string;
  after: string;
  block?: boolean;
}

const FORMAT_ACTIONS: Record<'mrkdwn' | 'markdown', FormatAction[]> = {
  mrkdwn: [
    { title: 'Bold', icon: Bold, before: '*', after: '*' },
    { title: 'Italic', icon: Italic, before: '_', after: '_' },
    { title: 'Strikethrough', icon: Strikethrough, before: '~', after: '~' },
    { title: 'Code', icon: Code, before: '`', after: '`' },
  ],
  markdown: [
    { title: 'Heading', icon: Heading1, before: '# ', after: '', block: true },
    { title: 'Subheading', icon: Heading2, before: '## ', after: '', block: true },
    { title: 'Bold', icon: Bold, before: '**', after: '**' },
    { title: 'Italic', icon: Italic, before: '*', after: '*' },
    { title: 'Bullet list', icon: List, before: '- ', after: '', block: true },
  ],
};

// ── Body field with formatting toolbar + rendered preview ─────────────────────

function FormattedField({
  label,
  value,
  onChange,
  placeholder,
  format,
  destinationName,
  rows = 8,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  format: 'mrkdwn' | 'markdown';
  destinationName: string;
  rows?: number;
}) {
  const taRef = useRef<HTMLTextAreaElement>(null);
  const [mode, setMode] = useState<'write' | 'preview'>('write');

  const apply = (a: FormatAction) => {
    const ta = taRef.current;
    if (!ta) return;
    const s = ta.selectionStart ?? value.length;
    const e = ta.selectionEnd ?? value.length;
    let next: string;
    let caret: number;
    if (a.block) {
      const lineStart = value.lastIndexOf('\n', s - 1) + 1;
      next = value.slice(0, lineStart) + a.before + value.slice(lineStart);
      caret = e + a.before.length;
    } else {
      const sel = value.slice(s, e) || 'text';
      next = value.slice(0, s) + a.before + sel + a.after + value.slice(e);
      caret = s + a.before.length + sel.length + a.after.length;
    }
    onChange(next);
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(caret, caret);
    });
  };

  const fieldCls =
    'w-full px-3.5 py-3 rounded-b-lg bg-gray-50 dark:bg-white/5 border border-t-0 border-gray-200 dark:border-white/10 text-body text-gray-800 dark:text-gray-200 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30 placeholder:text-gray-500 dark:placeholder:text-gray-400';

  return (
    <div>
      <label className="block text-meta text-gray-500 dark:text-gray-400 mb-1">{label}</label>
      {/* Toolbar row: format buttons left, write/preview toggle right */}
      <div className="flex items-center justify-between px-1.5 py-1 rounded-t-lg bg-gray-100 dark:bg-white/[0.07] border border-gray-200 dark:border-white/10">
        <div className="flex items-center gap-0.5">
          {FORMAT_ACTIONS[format].map((a) => (
            <button
              key={a.title}
              type="button"
              title={a.title}
              aria-label={a.title}
              disabled={mode === 'preview'}
              onClick={() => apply(a)}
              className="p-1.5 rounded-md text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-white dark:hover:bg-white/10 focus-visible:ring-2 focus-visible:ring-primary/50 transition-colors disabled:opacity-30"
            >
              <a.icon className="h-4 w-4" />
            </button>
          ))}
        </div>
        <div className="flex items-center gap-0.5">
          {([
            { m: 'write' as const, icon: PencilLine, t: 'Write' },
            { m: 'preview' as const, icon: Eye, t: 'Preview' },
          ]).map(({ m, icon: Icon, t }) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              className={`flex items-center gap-1 px-2 py-1 rounded-md text-meta font-medium focus-visible:ring-2 focus-visible:ring-primary/50 transition-colors ${
                mode === m
                  ? 'bg-white dark:bg-white/15 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <Icon className="h-3.5 w-3.5" />
              {t}
            </button>
          ))}
        </div>
      </div>
      {mode === 'write' ? (
        <textarea
          ref={taRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={rows}
          placeholder={placeholder}
          className={`${fieldCls} resize-y`}
        />
      ) : (
        <div className={`${fieldCls} min-h-[10rem] overflow-y-auto bg-white dark:bg-white/[0.03]`}>
          {value.trim() ? (
            format === 'mrkdwn' ? (
              <div
                className="text-body text-gray-800 dark:text-gray-200 leading-relaxed [&_a]:text-primary [&_.mrkdwn-code]:font-mono [&_.mrkdwn-code]:text-meta [&_.mrkdwn-code]:bg-gray-100 [&_.mrkdwn-code]:dark:bg-white/10 [&_.mrkdwn-code]:px-1 [&_.mrkdwn-code]:py-0.5 [&_.mrkdwn-code]:rounded [&_.mrkdwn-pre]:font-mono [&_.mrkdwn-pre]:text-meta [&_.mrkdwn-pre]:bg-gray-100 [&_.mrkdwn-pre]:dark:bg-white/10 [&_.mrkdwn-pre]:p-2 [&_.mrkdwn-pre]:rounded [&_.mrkdwn-pre]:my-1"
                dangerouslySetInnerHTML={{ __html: mrkdwnToHtml(value) }}
              />
            ) : (
              // format === 'markdown' is only ever used for create_doc — see
              // docsMarkdownToHtml above for why this can't be the generic
              // <Markdown> component.
              <div
                className="text-body text-gray-800 dark:text-gray-200 leading-relaxed space-y-1.5 [&_h1]:text-title [&_h1]:font-semibold [&_h1]:mt-2 [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:mt-2 [&_h3]:text-body [&_h3]:font-medium [&_h3]:mt-1.5 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-0.5 [&_ul_ul]:mt-0.5 [&_li]:leading-relaxed [&_hr]:border-gray-200 [&_hr]:dark:border-white/10 [&_hr]:my-3 [&_u]:underline [&_strong]:font-semibold [&_em]:italic [&_p]:my-1"
                dangerouslySetInnerHTML={{ __html: docsMarkdownToHtml(value) }}
              />
            )
          ) : (
            <p className="text-meta text-gray-400 italic">Nothing to preview yet.</p>
          )}
        </div>
      )}
      <p className="text-micro text-gray-400 dark:text-gray-500 mt-1">
        Formatting is limited to what {destinationName} can display.
      </p>
    </div>
  );
}

const PRIORITY_COLOURS = {
  high:   'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border-red-200 dark:border-red-900/30',
  medium: 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-900/30',
  low:    'bg-gray-50 dark:bg-white/5 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-white/10',
};

// ── Editable payload field ────────────────────────────────────────────────────

function PayloadField({
  label,
  value,
  onChange,
  multiline = false,
  placeholder,
  rows = 5,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  multiline?: boolean;
  placeholder?: string;
  rows?: number;
}) {
  const cls =
    'w-full px-3 py-2 rounded-lg bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-body text-gray-800 dark:text-gray-200 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30 placeholder:text-gray-500 dark:placeholder:text-gray-400';
  return (
    <div>
      <label className="block text-meta text-gray-500 dark:text-gray-400 mb-1">{label}</label>
      {multiline ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={rows}
          placeholder={placeholder}
          className={`${cls} resize-y`}
        />
      ) : (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={cls}
        />
      )}
    </div>
  );
}

// ── Result view ───────────────────────────────────────────────────────────────

function CompletedView({
  action,
  result,
  onClose,
}: {
  action: SuggestedAction;
  result: ActionResult;
  onClose: () => void;
}) {
  return (
    <div className="space-y-4">
      <div className="rounded-xl p-5 border bg-emerald-50/50 dark:bg-emerald-900/10 border-emerald-200 dark:border-emerald-800/40">
        <div className="flex items-center gap-2 mb-2">
          <CheckCircle2 className="h-4 w-4 text-emerald-500" />
          <span className="text-meta font-medium text-emerald-700 dark:text-emerald-400">Completed</span>
        </div>
        <p className="text-body text-gray-600 dark:text-gray-400">{action.description}</p>
      </div>

      {result.message && (
        <p className="text-meta text-gray-500 dark:text-gray-400">{result.message}</p>
      )}

      <div className="flex items-center justify-end gap-2 pt-1">
        {result.url && (
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => window.open(result.url, '_blank', 'noopener,noreferrer')}
            className="flex items-center gap-1.5 px-4 py-2 bg-primary text-white rounded-xl text-meta font-medium hover:bg-primary/90 transition-colors"
          >
            <ExternalLink className="h-4 w-4" />
            Open
          </motion.button>
        )}
        <button
          onClick={onClose}
          className="px-4 py-2 text-meta text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
        >
          Close
        </button>
      </div>
    </div>
  );
}

// ── Already-handled view (server completion, no session-local content) ────────
//
// Shown when the actionId's server completion is "done" or
// "needs-your-action" but this modal instance has no in-session result to
// show (a page reload happened, another user completed it, or this exact
// click was itself refused by claim-then-execute because someone beat it).
// The full drafted content/url from execution isn't persisted server-side
// (only state + who + when), so this is intentionally a summary, not a
// replay of ResultView.

function AlreadyHandledView({
  completion,
  onClose,
}: {
  completion: ActionCompletion;
  onClose: () => void;
}) {
  const isDone = completion.state === 'done';
  const Icon = isDone ? CheckCircle2 : AlertCircle;
  const tone = isDone
    ? 'bg-emerald-50/50 dark:bg-emerald-900/10 border-emerald-200 dark:border-emerald-800/40 text-emerald-700 dark:text-emerald-400'
    : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-900/30 text-blue-700 dark:text-blue-400';

  return (
    <div className="space-y-4">
      <div className={`rounded-xl p-5 border ${tone}`}>
        <div className="flex items-center gap-2 mb-2">
          <Icon className="h-4 w-4" />
          <span className="text-meta font-medium">
            {isDone ? 'Already completed' : 'Needs your action'}
          </span>
        </div>
        {completion.message && (
          <p className="text-body text-gray-600 dark:text-gray-400">{completion.message}</p>
        )}
        <p className="text-meta text-gray-500 dark:text-gray-400 mt-2">{completionByLine(completion)}</p>
      </div>

      <div className="flex items-center justify-end gap-2 pt-1">
        <button
          onClick={onClose}
          className="px-4 py-2 text-meta text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white focus-visible:ring-2 focus-visible:ring-primary/50 rounded-lg transition-colors"
        >
          Close
        </button>
      </div>
    </div>
  );
}

// ── Slack follow-up (shown after post_ticket_comment succeeds) ────────────────

function SlackFollowUp({ prefillMessage, projectId }: { prefillMessage: string; projectId?: string }) {
  const [expanded, setExpanded] = useState(false);
  const [mode, setMode] = useState<'dm' | 'channel'>('dm');
  const [recipient, setRecipient] = useState('');
  const [message, setMessage] = useState(prefillMessage.slice(0, 500));
  const [sent, setSent] = useState(false);

  const mutation = useMutation({
    mutationFn: () =>
      executeAction({
        type: mode === 'dm' ? 'slack_dm' : 'slack_message',
        payload: mode === 'dm'
          ? { userName: recipient, userEmail: recipient.includes('@') ? recipient : '', message }
          : { channel: recipient.startsWith('#') ? recipient : `#${recipient}`, message },
        projectId,
      }),
    onSuccess: () => setSent(true),
  });

  if (sent) {
    return (
      <div className="rounded-lg p-3 bg-[#4A154B]/10 border border-[#4A154B]/20 text-meta text-[#4A154B] dark:text-purple-300 flex items-center gap-2">
        <Check className="h-4 w-4 flex-shrink-0" /> Slack notification sent.
      </div>
    );
  }

  if (!expanded) {
    return (
      <button
        onClick={() => setExpanded(true)}
        className="w-full text-left rounded-lg px-3 py-2.5 border border-dashed border-gray-200 dark:border-white/10 text-meta text-gray-500 dark:text-gray-400 hover:border-[#4A154B]/40 hover:text-[#4A154B] dark:hover:text-purple-300 transition-colors flex items-center gap-2"
      >
        <MessageSquare className="h-4 w-4 flex-shrink-0" />
        Also notify via Slack?
      </button>
    );
  }

  return (
    <div className="rounded-xl border border-[#4A154B]/20 dark:border-purple-900/30 bg-[#4A154B]/5 dark:bg-purple-900/10 p-3 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-meta font-medium text-[#4A154B] dark:text-purple-300">
          <MessageSquare className="h-4 w-4" />
          Notify via Slack
        </div>
        <button onClick={() => setExpanded(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="flex gap-1">
        {(['dm', 'channel'] as const).map((m) => (
          <button
            key={m}
            onClick={() => setMode(m)}
            className={`px-2.5 py-1 rounded-lg text-meta font-medium transition-colors ${
              mode === m
                ? 'bg-[#4A154B] text-white'
                : 'bg-white dark:bg-white/5 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-white/10'
            }`}
          >
            {m === 'dm' ? 'Direct message' : 'Channel'}
          </button>
        ))}
      </div>

      <input
        type="text"
        value={recipient}
        onChange={(e) => setRecipient(e.target.value)}
        placeholder={mode === 'dm' ? 'Name or email address' : 'channel-name'}
        className="w-full px-2.5 py-1.5 rounded-lg bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 text-body text-gray-800 dark:text-gray-200 placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:outline-none focus:border-[#4A154B]/50 focus:ring-1 focus:ring-[#4A154B]/20"
      />

      <textarea
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        rows={3}
        className="w-full px-2.5 py-1.5 rounded-lg bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 text-body text-gray-800 dark:text-gray-200 resize-none focus:outline-none focus:border-[#4A154B]/50 focus:ring-1 focus:ring-[#4A154B]/20"
      />

      {mutation.isError && (
        <p className="text-meta text-red-600 dark:text-red-400">{(mutation.error as Error).message}</p>
      )}

      <div className="flex justify-end">
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending || !recipient.trim()}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-[#4A154B] text-white rounded-lg text-meta font-medium hover:bg-[#4A154B]/90 disabled:opacity-50 transition-colors"
        >
          {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          Send
        </motion.button>
      </div>
    </div>
  );
}

// ── Send email (shown after a status_report generated with format: email) ─────

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function SendEmailFollowUp({ content, projectId }: { content: string; projectId?: string }) {
  const { currentUser } = useUserStore();
  const [expanded, setExpanded] = useState(false);
  const [to, setTo] = useState(currentUser?.email ?? '');
  const [outcome, setOutcome] = useState<{ ok: boolean; message: string } | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      executeAction({
        type: 'send_generated_email',
        payload: { to, content },
        projectId,
      }),
    onSuccess: (data) => {
      setOutcome({ ok: data.outcome === 'sent', message: data.message });
      track('action.generated_email_sent', 'action', { projectId: projectId ?? '', succeeded: data.outcome === 'sent' });
    },
  });

  if (outcome?.ok) {
    return (
      <div className="rounded-lg p-3 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-900/30 text-meta text-emerald-700 dark:text-emerald-400 flex items-center gap-2">
        <Check className="h-4 w-4 flex-shrink-0" /> {outcome.message}
      </div>
    );
  }

  if (!expanded) {
    return (
      <button
        onClick={() => setExpanded(true)}
        className="w-full text-left rounded-lg px-3 py-2.5 border border-dashed border-gray-200 dark:border-white/10 text-meta text-gray-500 dark:text-gray-400 hover:border-primary/40 hover:text-primary transition-colors flex items-center gap-2"
      >
        <Mail className="h-4 w-4 flex-shrink-0" />
        Send as email?
      </button>
    );
  }

  const validEmail = EMAIL_RE.test(to.trim());

  return (
    <div className="rounded-xl border border-primary/20 dark:border-primary/30 bg-primary/5 dark:bg-primary/10 p-3 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-meta font-medium text-primary">
          <Mail className="h-4 w-4" />
          Send email
        </div>
        <button onClick={() => setExpanded(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
          <X className="h-4 w-4" />
        </button>
      </div>

      <input
        type="email"
        value={to}
        onChange={(e) => { setTo(e.target.value); setOutcome(null); }}
        placeholder="recipient@company.com"
        className="w-full px-2.5 py-1.5 rounded-lg bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 text-body text-gray-800 dark:text-gray-200 placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/20"
      />

      {outcome && !outcome.ok && (
        <p className="text-meta text-amber-600 dark:text-amber-400 flex items-start gap-1">
          <AlertCircle className="h-4 w-4 flex-shrink-0 mt-px" /> {outcome.message}
        </p>
      )}
      {mutation.isError && (
        <p className="text-meta text-red-600 dark:text-red-400 flex items-start gap-1">
          <AlertCircle className="h-4 w-4 flex-shrink-0 mt-px" /> {(mutation.error as Error).message}
        </p>
      )}

      <div className="flex justify-end">
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending || !validEmail}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-white rounded-lg text-meta font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors"
        >
          {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          Send
        </motion.button>
      </div>
    </div>
  );
}

// ── Result view ───────────────────────────────────────────────────────────────

function ResultView({
  result,
  onClose,
  onDone,
  action,
  projectId,
  actionId,
  actionType,
  submittedFormat,
}: {
  result: ActionResult;
  onClose: () => void;
  onDone?: (result: ActionResult) => void;
  action: SuggestedAction;
  projectId?: string;
  actionId: string;
  actionType: string;
  submittedFormat?: string;
}) {
  const [copied, setCopied] = useState(false);
  const [copiedPlain, setCopiedPlain] = useState(false);

  const handleCopy = () => {
    if (result.content) {
      void navigator.clipboard.writeText(result.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      track('action.content_copied', 'action', { actionId, actionType });
    }
  };

  const handleCopyPlainText = () => {
    if (result.content) {
      void navigator.clipboard.writeText(markdownToPlainText(result.content));
      setCopiedPlain(true);
      setTimeout(() => setCopiedPlain(false), 2000);
      track('action.content_copied_plain_text', 'action', { actionId, actionType });
    }
  };

  const handleOpenUrl = () => {
    if (result.url) {
      window.open(result.url, '_blank', 'noopener,noreferrer');
      track('action.url_opened', 'action', { actionId, actionType });
    }
  };

  return (
    <div className="space-y-4">
      <div className={`rounded-xl p-5 border text-body ${
        result.outcome === 'sent'
          ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-900/30 text-emerald-700 dark:text-emerald-400'
          : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-900/30 text-blue-700 dark:text-blue-400'
      }`}>
        {result.outcome === 'sent' && <Check className="inline h-4 w-4 mr-1.5" />}
        {result.message}
      </div>

      {result.content && (
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <p className="text-meta text-gray-500 dark:text-gray-400">Content to use</p>
            <div className="flex items-center gap-3">
              <button
                onClick={handleCopyPlainText}
                title="Copy without markdown formatting — for pasting into email etc."
                className="flex items-center gap-1 text-meta text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
              >
                {copiedPlain ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                {copiedPlain ? 'Copied' : 'Copy as plain text'}
              </button>
              <button
                onClick={handleCopy}
                className="flex items-center gap-1 text-meta text-primary hover:text-primary/80 transition-colors"
              >
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
          </div>
          <div className="rounded-lg bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 p-4 max-h-80 overflow-y-auto">
            {/* Keyed off what the destination actually renders, not just a
                slack/everything-else split — a plain-text destination
                (Slides outline, Calendar description, a Jira/Linear
                comment) showing styled headings and bold here would be
                previewing formatting that never survives at the real
                destination. */}
            {actionType.startsWith('slack') || submittedFormat === 'slack' ? (
              // Slack-bound content is mrkdwn, not markdown — preview it
              // the way Slack will actually render it.
              <div
                className="text-meta text-gray-800 dark:text-gray-200 leading-relaxed [&_a]:text-primary [&_.mrkdwn-code]:font-mono [&_.mrkdwn-code]:bg-gray-100 [&_.mrkdwn-code]:dark:bg-white/10 [&_.mrkdwn-code]:px-1 [&_.mrkdwn-code]:rounded [&_.mrkdwn-pre]:font-mono [&_.mrkdwn-pre]:bg-gray-100 [&_.mrkdwn-pre]:dark:bg-white/10 [&_.mrkdwn-pre]:p-2 [&_.mrkdwn-pre]:rounded"
                dangerouslySetInnerHTML={{ __html: mrkdwnToHtml(result.content) }}
              />
            ) : actionType === 'create_doc' ? (
              // Only reached on failure/not-connected — result.content here
              // IS the LLM-expanded structured_content _build_doc_requests
              // would have parsed, so this preview is accurate to what a
              // successful create would have produced. See
              // docsMarkdownToHtml above.
              <div
                className="text-meta text-gray-800 dark:text-gray-200 leading-relaxed space-y-1.5 [&_h1]:text-body [&_h1]:font-semibold [&_h1]:mt-2 [&_h2]:text-meta [&_h2]:font-semibold [&_h2]:mt-2 [&_h3]:text-meta [&_h3]:font-medium [&_h3]:mt-1.5 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-0.5 [&_ul_ul]:mt-0.5 [&_li]:leading-relaxed [&_hr]:border-gray-200 [&_hr]:dark:border-white/10 [&_hr]:my-3 [&_u]:underline [&_strong]:font-semibold [&_em]:italic [&_p]:my-1"
                dangerouslySetInnerHTML={{ __html: docsMarkdownToHtml(result.content) }}
              />
            ) : actionType === 'create_slides' || actionType === 'create_calendar_event' || actionType === 'post_ticket_comment' ? (
              // These destinations never render markdown at all (a raw
              // Slides text box, a Calendar description field, or a Jira
              // plain-text ADF paragraph / Linear comment body) — showing
              // this as styled markdown would preview formatting that will
              // not survive. Preformatted plain text is the honest preview.
              <pre className="text-meta text-gray-800 dark:text-gray-200 leading-relaxed whitespace-pre-wrap font-sans">{result.content}</pre>
            ) : (
              <Markdown size="xs">{result.content}</Markdown>
            )}
          </div>
        </div>
      )}

      {actionType === 'post_ticket_comment' && result.outcome === 'sent' && (
        <SlackFollowUp
          prefillMessage={String(action.payload?.comment ?? '')}
          projectId={projectId}
        />
      )}

      {actionType === 'status_report' && submittedFormat === 'email' && !!result.content && (
        <SendEmailFollowUp content={result.content} projectId={projectId} />
      )}

      <StarRating
        featureKey={FEATURE_KEYS.ACTION_MODAL}
        label="How well did this work?"
        onSubmitted={() => {}}
      />

      <div className="flex items-center justify-end gap-2 pt-1">
        {result.url && (
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleOpenUrl}
            className="flex items-center gap-1.5 px-4 py-2 bg-primary text-white rounded-xl text-meta font-medium hover:bg-primary/90 transition-colors"
          >
            <ExternalLink className="h-4 w-4" />
            Open
          </motion.button>
        )}
        <button
          onClick={() => (onDone ? onDone(result) : onClose())}
          className="px-4 py-2 text-meta text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
        >
          Done
        </button>
      </div>
    </div>
  );
}

// ── Slack channel picker dropdown ─────────────────────────────────────────────

function SlackChannelPicker({
  projectId,
  value,
  onChange,
}: {
  projectId?: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const { data: channels, isLoading, isError } = useQuery({
    queryKey: queryKeys.integrationResources('slack'),
    queryFn: () => getIntegrationResources('slack'),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const { data: project } = useQuery({
    queryKey: queryKeys.project(projectId!),
    queryFn: () => getProject(projectId!),
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000,
  });

  // Pre-select once channels load: prefer the project-linked channel, then
  // an exact match on the AI-suggested channel name. Deliberately does NOT
  // fall back to "the first channel in the list" — a channel merely
  // existing in the workspace is not evidence it's the right destination
  // for this project (the same no-guessing rule the backend's SLACK TARGET
  // prompt directive enforces). Leaving the field unselected surfaces a
  // visible "choose one" prompt below instead of a silent, possibly-wrong
  // default; _exec_slack_message also refuses to send with no channel.
  useEffect(() => {
    if (!channels?.length) return;
    // Already pointing at a valid channel ID — don't override
    if (value && channels.some((c) => c.id === value)) return;

    const projectLink = project?.links?.find((l) => l.provider === 'slack');
    if (projectLink) {
      const linked = channels.find((c) => c.id === projectLink.externalResourceId);
      if (linked) { onChange(linked.id); return; }
    }

    // Try matching the AI-suggested channel name
    if (value) {
      const normalized = value.startsWith('#') ? value.slice(1) : value;
      const match = channels.find(
        (c) => c.id === value || c.name === value || c.name === `#${normalized}`,
      );
      if (match) { onChange(match.id); return; }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channels, project]);

  const inputCls =
    'w-full px-3 py-2 rounded-lg bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-body text-gray-800 dark:text-gray-200 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30';

  if (isLoading) {
    return (
      <div>
        <label className="block text-meta text-gray-500 dark:text-gray-400 mb-1">Channel</label>
        <div className={`${inputCls} flex items-center gap-2 text-gray-400`}>
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading channels…
        </div>
      </div>
    );
  }

  // Slack not connected or API error — fall back to free text
  if (isError || !channels?.length) {
    return (
      <PayloadField label="Channel" value={value} onChange={onChange} placeholder="channel-name" />
    );
  }

  const projectLink = project?.links?.find((l) => l.provider === 'slack');
  const selectedIsProjectLinked = !!projectLink && channels.find((c) => c.id === value)?.id === projectLink.externalResourceId;
  // The Hash icon to the left of the field is the single "#" decoration.
  // Channel names from the API already carry their own leading "#" (see
  // backend/app/routers/integrations.py, backend/app/workers/actions.py
  // _fetch_slack_channels), so it must be stripped here or the field reads
  // as "# #poc2026" — icon plus a name that duplicates it. The stored/
  // selected value is unaffected: options are keyed by channel id, never
  // by this display string.
  const stripHash = (name: string) => (name.startsWith('#') ? name.slice(1) : name);
  // A value that isn't (yet, or ever) one of the real channel ids must not
  // be handed to the native <select> as-is — an unmatched value renders
  // inconsistently across browsers. Fall back to "" so the placeholder
  // option below is what's shown instead of a stale AI-suggested string.
  const selectValue = channels.some((c) => c.id === value) ? value : '';

  return (
    <div>
      <label className="block text-meta text-gray-500 dark:text-gray-400 mb-1">Channel</label>
      <div className="relative">
        <Hash className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
        <select
          value={selectValue}
          onChange={(e) => onChange(e.target.value)}
          className={`${inputCls} pl-8 appearance-none cursor-pointer`}
        >
          {!selectValue && <option value="" disabled>Select a channel…</option>}
          {channels.map((c) => (
            <option key={c.id} value={c.id}>{stripHash(c.name)}</option>
          ))}
        </select>
      </div>
      {selectedIsProjectLinked && (
        <p className="text-micro text-gray-400 dark:text-gray-500 mt-1 flex items-center gap-1">
          <Check className="h-4 w-4 text-emerald-500" />
          Project-linked channel
        </p>
      )}
      {!selectValue && (
        <p className="text-micro text-amber-500 dark:text-amber-400 mt-1">
          No channel selected — choose one before sending.
        </p>
      )}
    </div>
  );
}

// ── Editable payload section per action type ──────────────────────────────────

function PayloadEditor({
  action,
  edited,
  setEdited,
  projectId,
}: {
  action: SuggestedAction;
  edited: Record<string, string>;
  setEdited: (k: string, v: string) => void;
  projectId?: string;
}) {
  const p = action.payload;

  switch (action.type) {
    case 'slack_message':
      return (
        <div className="space-y-4">
          <SlackChannelPicker
            projectId={projectId}
            value={edited.channel ?? String(p.channel ?? '')}
            onChange={(v) => setEdited('channel', v)}
          />
          <FormattedField label="Message" value={edited.message ?? String(p.message ?? '')} onChange={(v) => setEdited('message', v)} format="mrkdwn" destinationName="Slack" rows={8} placeholder="Type your message…" />
        </div>
      );
    case 'slack_dm':
      return (
        <div className="space-y-4">
          <PayloadField label="To" value={edited.userName ?? String(p.userName ?? '')} onChange={(v) => setEdited('userName', v)} placeholder="Team member name" />
          <FormattedField label="Message" value={edited.message ?? String(p.message ?? '')} onChange={(v) => setEdited('message', v)} format="mrkdwn" destinationName="Slack" rows={8} placeholder="Type your message…" />
        </div>
      );
    case 'create_doc':
      return (
        <div className="space-y-4">
          <PayloadField label="Document title" value={edited.title ?? String(p.title ?? '')} onChange={(v) => setEdited('title', v)} />
          <FormattedField label="Draft content" value={edited.content ?? String(p.content ?? '')} onChange={(v) => setEdited('content', v)} format="markdown" destinationName="Google Docs" rows={14} placeholder="Draft the document…" />
        </div>
      );
    case 'create_slides':
      return (
        <div className="space-y-4">
          <PayloadField label="Presentation title" value={edited.title ?? String(p.title ?? '')} onChange={(v) => setEdited('title', v)} />
          <PayloadField label="Outline" value={edited.outline ?? String(p.outline ?? '')} onChange={(v) => setEdited('outline', v)} multiline rows={12} placeholder="Slide outline…" />
        </div>
      );
    case 'create_calendar_event':
      return (
        <div className="space-y-4">
          <PayloadField label="Event title" value={edited.title ?? String(p.title ?? '')} onChange={(v) => setEdited('title', v)} />
          <PayloadField label="Description / agenda" value={edited.description ?? String(p.description ?? '')} onChange={(v) => setEdited('description', v)} multiline />
          <PayloadField label="Attendees (comma-separated emails)" value={edited.attendeeEmails ?? String((p.attendeeEmails as string[] | undefined ?? []).join(', '))} onChange={(v) => setEdited('attendeeEmails', v)} placeholder="alice@co.com, bob@co.com" />
          <PayloadField label="Duration (minutes)" value={edited.durationMinutes ?? String(p.durationMinutes ?? 60)} onChange={(v) => setEdited('durationMinutes', v)} />
        </div>
      );
    case 'post_ticket_comment':
      return (
        <div className="space-y-4">
          <PayloadField label="Comment" value={edited.comment ?? String(p.comment ?? '')} onChange={(v) => setEdited('comment', v)} multiline rows={8} placeholder="Add your comment…" />
        </div>
      );
    case 'update_ticket_status':
      return (
        <div className="space-y-4">
          <label className="block text-meta text-gray-500 dark:text-gray-400 mb-1">New status</label>
          <select
            value={edited.newStatus ?? String(p.newStatus ?? 'on_track')}
            onChange={(e) => setEdited('newStatus', e.target.value)}
            className="w-full px-3 py-2 rounded-lg bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-body text-gray-800 dark:text-gray-200 focus:outline-none"
          >
            {['triage', 'planning', 'on_track', 'at_risk', 'complete'].map((s) => (
              <option key={s} value={s}>{s.replace('_', ' ')}</option>
            ))}
          </select>
        </div>
      );
    case 'status_report':
      return (
        <div className="space-y-4">
          <div>
            <label className="block text-meta text-gray-500 dark:text-gray-400 mb-1">Format</label>
            <select
              value={edited.format ?? String(p.format ?? 'markdown')}
              onChange={(e) => setEdited('format', e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-body text-gray-800 dark:text-gray-200 focus:outline-none"
            >
              <option value="markdown">Markdown</option>
              <option value="slack">Slack</option>
              <option value="email">Email</option>
            </select>
          </div>
          <div>
            <label className="block text-meta text-gray-500 dark:text-gray-400 mb-1">Audience</label>
            <select
              value={edited.audience ?? String(p.audience ?? 'team')}
              onChange={(e) => setEdited('audience', e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-body text-gray-800 dark:text-gray-200 focus:outline-none"
            >
              <option value="team">Team</option>
              <option value="stakeholder">Stakeholder</option>
              <option value="executive">Executive</option>
            </select>
          </div>
        </div>
      );
    default:
      return null;
  }
}

// ── Main modal ────────────────────────────────────────────────────────────────

export function ActionModal({ action, projectId, ticketId, onClose, onCompleted, completedResult, completion }: ActionModalProps) {
  const meta = ACTION_META[action.type] ?? ACTION_META.open_url;
  const Icon = meta.icon;
  const qc = useQueryClient();

  const [edited, setEditedState] = useState<Record<string, string>>({});
  const setEdited = (k: string, v: string) => setEditedState((p) => ({ ...p, [k]: v }));
  const [result, setResult] = useState<ActionResult | null>(null);

  // status_report's destination follows the chosen format — a Slack-bound
  // report should read as Slack-bound at a glance. Other types carry a
  // fixed destination from ACTION_META. (The email format keeps the neutral
  // Mail glyph on purpose: delivery is via Resend, not Gmail — showing
  // Gmail's mark would misstate the destination.)
  const reportFormat = action.type === 'status_report'
    ? (edited.format ?? String(action.payload?.format ?? 'markdown'))
    : null;
  const effective = useMemo(() => {
    if (reportFormat === 'slack') return { brand: SLACK_BRAND as ActionBrand | undefined, icon: MessageSquare, label: 'Status Report → Slack', colour: 'text-[#4A154B]' };
    if (reportFormat === 'email') return { brand: undefined as ActionBrand | undefined, icon: Mail, label: 'Status Report → Email', colour: 'text-gray-600' };
    return { brand: meta.brand, icon: Icon, label: meta.label, colour: meta.colour };
  }, [reportFormat, meta, Icon]);
  const EffectiveIcon = effective.icon;

  const mountTsRef = useRef(Date.now());
  const executedRef = useRef(false);

  useEffect(() => {
    track('action.modal.opened', 'feature_use', {
      actionId: action.actionId,
      actionType: action.type,
      ...(projectId ? { projectId } : {}),
      ...(ticketId ? { ticketId } : {}),
    });

    return () => {
      if (!executedRef.current) {
        track('action.modal.abandoned', 'abandonment', {
          actionId: action.actionId,
          actionType: action.type,
          durationMs: Date.now() - mountTsRef.current,
        });
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const mutation = useMutation({
    mutationFn: () => {
      // Merge edited values back into payload
      const mergedPayload: Record<string, unknown> = { ...action.payload };
      for (const [k, v] of Object.entries(edited)) {
        if (k === 'attendeeEmails') {
          mergedPayload[k] = v.split(',').map((e) => e.trim()).filter(Boolean);
        } else if (k === 'durationMinutes') {
          mergedPayload[k] = parseInt(v, 10) || 60;
        } else {
          mergedPayload[k] = v;
        }
      }
      return executeAction({ type: action.type, payload: mergedPayload, projectId, ticketId, actionId: action.actionId });
    },
    onSuccess: (data) => {
      executedRef.current = true;
      track(data.alreadyCompleted ? 'action.already_completed' : 'action.executed', 'action', {
        actionId: action.actionId,
        actionType: action.type,
        ...(projectId ? { projectId } : {}),
        ...(ticketId ? { ticketId } : {}),
        durationMs: Date.now() - mountTsRef.current,
      });
      setResult(data);
      // For open_url or url outcomes, auto-open — but never for a
      // refused-reclaim response (alreadyCompleted): opening a URL we
      // didn't actually get back for this call would be wrong, and
      // alreadyCompleted responses don't carry `url` anyway.
      if (!data.alreadyCompleted && data.outcome === 'url' && data.url && action.type === 'open_url') {
        window.open(data.url, '_blank', 'noopener,noreferrer');
      }
      // Source of truth for "done/needs-your-action/failed" on every
      // trigger (row/card/pill) is the server completions map, not this
      // modal's local state — refetch it so it reflects the write we just
      // made, including across a page reload later. Harmless to invalidate
      // whats-on-deck unconditionally; the other two are scoped to what we
      // actually know from props.
      void qc.invalidateQueries({ queryKey: queryKeys.whatsOnDeck });
      if (projectId) void qc.invalidateQueries({ queryKey: queryKeys.risk(projectId) });
      if (projectId && ticketId) void qc.invalidateQueries({ queryKey: queryKeys.tickets(projectId) });
    },
  });

  // "done" / "needs-your-action" are non-retryable server-side (claim-then-
  // execute refuses to re-dispatch) — reopening one of those without a
  // fresh in-session result (page reload, another user completed it, or
  // this very click got refused because someone beat it) shows a read-only
  // summary instead of the editable form. "failed" always falls through to
  // the normal editable form below, with a retry banner.
  const activeCompletion = result?.completion ?? completion;
  const showAlreadyHandled =
    result?.alreadyCompleted === true ||
    (!result && !completedResult && isBlockingCompletion(completion));

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 dark:bg-black/60 backdrop-blur-sm"
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 8 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 8 }}
          transition={{ type: 'spring', damping: 30, stiffness: 400 }}
          className={`w-full ${MODAL_WIDTH[meta.width ?? 'md']} max-h-[90vh] overflow-y-auto bg-white dark:bg-[hsl(220,15%,12%)] rounded-2xl shadow-2xl border border-gray-200 dark:border-white/10`}
        >
          {/* Header */}
          <div className="flex items-start justify-between gap-3 p-6 border-b border-gray-100 dark:border-white/5">
            <div className="flex items-start gap-3.5">
              {/* Destination tile — the official mark of the app this action
                  targets, or a neutral glyph when the destination is internal
                  or ambiguous. The user should know at a glance where this
                  content is going. */}
              {effective.brand ? (
                <div className="mt-0.5 h-11 w-11 rounded-xl bg-white dark:bg-white/95 ring-1 ring-gray-200 dark:ring-white/20 shadow-sm flex items-center justify-center flex-shrink-0">
                  <img src={effective.brand.src} srcSet={effective.brand.srcSet} alt="" className="h-6 w-6 object-contain" />
                </div>
              ) : (
                <div className={`mt-0.5 h-11 w-11 rounded-xl bg-gray-50 dark:bg-white/5 ring-1 ring-gray-200 dark:ring-white/10 flex items-center justify-center flex-shrink-0 ${effective.colour ?? meta.colour}`}>
                  <EffectiveIcon className="h-5 w-5" />
                </div>
              )}
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-title text-gray-900 dark:text-white">{action.label}</h2>
                  <span className={`text-meta font-semibold px-1.5 py-0.5 rounded-full border capitalize ${PRIORITY_COLOURS[action.priority]}`}>
                    {action.priority}
                  </span>
                </div>
                <p className="text-meta text-gray-500 dark:text-gray-400 mt-1">{action.description}</p>
                <p className="text-meta text-gray-400 dark:text-gray-500 mt-0.5 font-medium uppercase tracking-wide">{effective.label}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              aria-label="Close"
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-white/5 focus-visible:ring-2 focus-visible:ring-primary/50 transition-colors flex-shrink-0"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Body */}
          <div className="p-6">
            {completedResult ? (
              <CompletedView
                action={action}
                result={completedResult}
                onClose={onClose}
              />
            ) : showAlreadyHandled && activeCompletion ? (
              <AlreadyHandledView completion={activeCompletion} onClose={onClose} />
            ) : result ? (
              <ResultView
                result={result}
                onClose={onClose}
                onDone={(r) => { onCompleted?.(r); onClose(); }}
                action={action}
                projectId={projectId}
                actionId={action.actionId}
                actionType={action.type}
                submittedFormat={edited.format ?? String(action.payload?.format ?? '')}
              />
            ) : (
              <div className="space-y-4">
                {/* Retryable: the backend only blocks re-claiming "done" and
                    "needs-your-action" — a "failed" completion is not a
                    dead end, so this stays the normal editable form with a
                    visible (not de-emphasized) notice, not a separate
                    read-only screen. */}
                {completion?.state === 'failed' && (
                  <div className="flex items-start gap-2 rounded-lg p-3 border bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-900/30 text-red-700 dark:text-red-400">
                    <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                    <div className="min-w-0">
                      <p className="text-meta font-medium">Previous attempt failed</p>
                      {completion.message && <p className="text-meta">{completion.message}</p>}
                    </div>
                  </div>
                )}

                <PayloadEditor action={action} edited={edited} setEdited={setEdited} projectId={projectId} />

                {mutation.isError && (
                  <p className="text-meta text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/30 rounded-lg p-2.5">
                    {(mutation.error as Error).message}
                  </p>
                )}

                <div className="flex items-center justify-end gap-2 pt-1">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 text-meta text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white focus-visible:ring-2 focus-visible:ring-primary/50 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => mutation.mutate()}
                    disabled={mutation.isPending}
                    className="flex items-center gap-1.5 px-4 py-2 bg-primary text-white rounded-xl text-meta font-medium hover:bg-primary/90 focus-visible:ring-2 focus-visible:ring-primary/50 transition-colors disabled:opacity-50"
                  >
                    {mutation.isPending
                      ? <><Loader2 className="h-4 w-4 animate-spin" /> Working…</>
                      : completion?.state === 'failed'
                      ? <><RefreshCw className="h-4 w-4" /> Retry</>
                      : <><Send className="h-4 w-4" /> Execute</>
                    }
                  </motion.button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// ── Standalone action button (pill) ──────────────────────────────────────────

interface ActionButtonProps {
  action: SuggestedAction;
  projectId?: string;
  ticketId?: string;
  compact?: boolean;
  onBeforeOpen?: (action: SuggestedAction) => void;
  /** Server completions map from the source cache doc (see ActionModalProps.completion) — looked up by this action's actionId. */
  completions?: Record<string, ActionCompletion>;
}

export function ActionButton({ action, projectId, ticketId, compact = false, onBeforeOpen, completions }: ActionButtonProps) {
  const [open, setOpen] = useState(false);
  const { markDone, getResult } = useActionStore();
  const completedResult = getResult(action.actionId);
  const meta = ACTION_META[action.type] ?? ACTION_META.open_url;
  const Icon = meta.icon;
  const completion = completions?.[action.actionId];
  const triggerState = deriveActionTriggerState(completion);

  const priorityCls =
    action.priority === 'high'
      ? 'border-red-200 dark:border-red-900/30 text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
      : action.priority === 'medium'
      ? 'border-amber-200 dark:border-amber-900/30 text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/10'
      : 'border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-white/5';

  // "done-this-batch" is the only de-emphasized state (existing opacity-60
  // treatment). "needs-your-action" and "failed" must stay visually normal
  // — they're not closed loops, someone still needs to act.
  const stateCls =
    triggerState === 'done-this-batch'
      ? 'border-gray-200 dark:border-white/[0.07] text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-white/[0.03]'
      : triggerState === 'needs-your-action'
      ? 'border-blue-200 dark:border-blue-900/40 text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/10 hover:bg-blue-100 dark:hover:bg-blue-900/20'
      : triggerState === 'failed'
      ? 'border-red-200 dark:border-red-900/40 text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/10 hover:bg-red-100 dark:hover:bg-red-900/20'
      : `${priorityCls} ${compact ? '' : 'bg-white dark:bg-transparent'}`;

  return (
    <>
      <button
        onClick={() => { onBeforeOpen?.(action); setOpen(true); }}
        title={completion ? completionByLine(completion) : action.description}
        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-meta font-medium focus-visible:ring-2 focus-visible:ring-primary/50 transition-colors ${stateCls}`}
      >
        {triggerState === 'done-this-batch' ? <Check className="h-4 w-4 flex-shrink-0 text-emerald-500" />
          : triggerState === 'needs-your-action' ? <AlertCircle className="h-4 w-4 flex-shrink-0" />
          : triggerState === 'failed' ? <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          : <Icon className="h-4 w-4 flex-shrink-0" />
        }
        <span className={`truncate max-w-[180px] ${triggerState === 'done-this-batch' ? 'opacity-60' : ''}`}>{action.label}</span>
        <ChevronRight className="h-4 w-4 flex-shrink-0 opacity-50" />
      </button>
      {completion && (triggerState === 'done-this-batch' || triggerState === 'needs-your-action') && (
        <p className="text-micro text-gray-400 dark:text-gray-500 mt-0.5">{completionByLine(completion)}</p>
      )}

      {open && (
        <ActionModal
          action={action}
          projectId={projectId}
          ticketId={ticketId}
          completedResult={completedResult}
          completion={completion}
          onCompleted={(r) => { markDone(action.actionId, r); setOpen(false); }}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

// ── Action bar (group) ────────────────────────────────────────────────────────

interface ActionBarProps {
  actions: SuggestedAction[];
  role?: string;
  projectId?: string;
  ticketId?: string;
  compact?: boolean;
  max?: number;
  onBeforeOpen?: (action: SuggestedAction) => void;
  completions?: Record<string, ActionCompletion>;
}

export function ActionBar({ actions, role, projectId, ticketId, compact = false, max = 4, onBeforeOpen, completions }: ActionBarProps) {
  const roleLower = role?.toLowerCase();
  const visible = roleLower
    ? actions.filter((a) =>
        roleLower === 'admin' ||
        !a.roles?.length ||
        a.roles.some((r) => r.toLowerCase() === roleLower) ||
        a.roles.some((r) => r.toLowerCase() === 'admin'),
      )
    : actions;

  if (!visible.length) return null;

  const shown = visible.slice(0, max);

  return (
    <div className="flex flex-wrap gap-1.5">
      {shown.map((a) => (
        <ActionButton key={a.actionId} action={a} projectId={projectId} ticketId={ticketId} compact={compact} onBeforeOpen={onBeforeOpen} completions={completions} />
      ))}
    </div>
  );
}
